import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Json } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, Fuel, Info } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Vehicle {
  id: string;
  name: string;
  license_plate: string | null;
}

interface VehicleTotal {
  license_plate: string;
  total_amount: number;
  position_count: number;
  fuel_types: string[];
  total_liters: number | null;
}

interface CustomerCost {
  description: string;
  amount: number;
}

interface ExtractedData {
  invoice_number: string | null;
  invoice_date: string | null;
  total_amount: number | null;
  vehicle_totals: VehicleTotal[];
  customer_costs: CustomerCost[];
}

interface DKVInvoiceUploadProps {
  vehicles: Vehicle[];
  onImportComplete: () => void;
}

export function DKVInvoiceUpload({ vehicles, onImportComplete }: DKVInvoiceUploadProps) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const normalizePlate = (plate: string | null | undefined): string => {
    if (!plate) return "";
    return plate.toUpperCase().replace(/[\s-]/g, "");
  };

  const findMatchingVehicle = (licensePlate: string | null | undefined): Vehicle | null => {
    if (!licensePlate) return null;
    const normalized = normalizePlate(licensePlate);
    return vehicles.find(v =>
      v.license_plate && normalizePlate(v.license_plate) === normalized
    ) || null;
  };

  const getVehicleTotalsWithMatching = () => {
    if (!extractedData?.vehicle_totals) return [];
    return extractedData.vehicle_totals.map(vt => ({
      ...vt,
      matchedVehicle: findMatchingVehicle(vt.license_plate)
    }));
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setExtractedData(null);
    setError(null);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error("Nicht authentifiziert");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-dkv-invoice`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error("Rate-Limit erreicht. Bitte versuchen Sie es später erneut.");
        }
        if (response.status === 402) {
          throw new Error("Kontingent erschöpft. Bitte laden Sie Guthaben auf.");
        }
        throw new Error(errorData.error || "Fehler beim Verarbeiten der Rechnung");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Unbekannter Fehler");
      }

      // Normalize: ensure arrays exist
      const data = result.data;
      if (!data.vehicle_totals) data.vehicle_totals = [];
      if (!data.customer_costs) data.customer_costs = [];

      setExtractedData(data);
      toast.success(`${data.vehicle_totals.length} Fahrzeuge und ${data.customer_costs.length} allgemeine Kosten extrahiert`);
    } catch (err) {
      console.error("Error uploading DKV invoice:", err);
      const errorMessage = err instanceof Error ? err.message : "Fehler beim Upload";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleSavePositions = async () => {
    if (!extractedData || !user) return;

    const vehicleTotalsWithMatching = getVehicleTotalsWithMatching();
    const matchedTotals = vehicleTotalsWithMatching.filter(p => p.matchedVehicle);
    const customerCosts = extractedData.customer_costs || [];
    const customerCostTotal = customerCosts.reduce((sum, c) => sum + c.amount, 0);

    if (matchedTotals.length === 0 && customerCostTotal === 0) {
      toast.error("Keine importierbaren Kosten gefunden");
      return;
    }

    setSaving(true);

    try {
      const costsToInsert: Array<{
        vehicle_id: string;
        cost_type: string;
        amount: number;
        cost_date: string;
        description: string;
        created_by: string;
      }> = [];

      // 1. Vehicle-specific fuel totals
      for (const vt of matchedTotals) {
        costsToInsert.push({
          vehicle_id: vt.matchedVehicle!.id,
          cost_type: "treibstoff",
          amount: vt.total_amount,
          cost_date: extractedData.invoice_date || new Date().toISOString().split("T")[0],
          description: [
            `DKV Gesamt (${vt.position_count} Positionen)`,
            vt.fuel_types?.length ? vt.fuel_types.join(", ") : null,
            vt.total_liters ? `${vt.total_liters.toFixed(1)}L gesamt` : null,
            `Kennzeichen: ${vt.license_plate}`
          ].filter(Boolean).join(" - "),
          created_by: user.id,
        });
      }

      // 2. Customer costs distributed across ALL matched vehicles
      if (customerCostTotal > 0 && matchedTotals.length > 0) {
        const perVehicleShare = customerCostTotal / matchedTotals.length;
        const customerCostDesc = customerCosts.map(c => `${c.description}: ${c.amount.toFixed(2)}€`).join(", ");

        for (const vt of matchedTotals) {
          costsToInsert.push({
            vehicle_id: vt.matchedVehicle!.id,
            cost_type: "sonstiges",
            amount: Math.round(perVehicleShare * 100) / 100,
            cost_date: extractedData.invoice_date || new Date().toISOString().split("T")[0],
            description: `DKV Kundenbezogene Kosten (anteilig ${matchedTotals.length} Fzg.) - ${customerCostDesc}`,
            created_by: user.id,
          });
        }
      }

      if (costsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("vehicle_one_time_costs")
          .insert(costsToInsert);

        if (insertError) throw insertError;
      }

      // Log the import
      await supabase.from("dkv_invoice_imports").insert([{
        file_name: selectedFile?.name || "unknown",
        invoice_date: extractedData.invoice_date,
        invoice_number: extractedData.invoice_number,
        total_amount: extractedData.total_amount,
        status: "processed",
        extracted_data: JSON.parse(JSON.stringify(extractedData)) as Json,
        matched_count: matchedTotals.length,
        unmatched_count: vehicleTotalsWithMatching.length - matchedTotals.length,
        created_by: user.id,
        processed_at: new Date().toISOString(),
      }]);

      toast.success(`${costsToInsert.length} Kostenpositionen importiert`);
      setSelectedFile(null);
      setExtractedData(null);
      onImportComplete();
    } catch (err) {
      console.error("Error saving positions:", err);
      toast.error("Fehler beim Speichern der Kosten");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setError(null);
  };

  const vehicleTotalsWithMatching = getVehicleTotalsWithMatching();
  const matchedCount = vehicleTotalsWithMatching.filter(p => p.matchedVehicle).length;
  const unmatchedCount = vehicleTotalsWithMatching.length - matchedCount;
  const customerCosts = extractedData?.customer_costs || [];
  const customerCostTotal = customerCosts.reduce((sum, c) => sum + c.amount, 0);

  const DKV_IMPORT_FIELDS = [
    { label: "Rechnungsnummer", required: false },
    { label: "Rechnungsdatum", required: false },
    { label: "Gesamtbetrag", required: false },
    { label: "Kennzeichen", required: true },
    { label: "Summe pro Fahrzeug", required: true },
    { label: "Kraftstoffarten", required: false },
    { label: "Gesamtliter", required: false },
    { label: "Kundenbezogene Kosten", required: false },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fuel className="h-5 w-5" />
            DKV-Rechnung Import
          </CardTitle>
          <CardDescription>
            Laden Sie DKV-Rechnungen hoch. Kosten werden pro Fahrzeug summiert importiert.
            Kundenbezogene Kosten werden anteilig auf alle Fahrzeuge verteilt.
            Aktuell sind {vehicles.length} Fahrzeuge für die Zuordnung verfügbar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FileUploadDropzone
            accept=".pdf,.png,.jpg,.jpeg"
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            onClear={handleClearFile}
            title="DKV-Rechnung hochladen"
            description="Ziehen Sie eine PDF- oder Bilddatei hierher oder klicken Sie zum Auswählen"
            acceptedFormats={["PDF", "PNG", "JPG", "JPEG"]}
            supportedFields={DKV_IMPORT_FIELDS}
            disabled={uploading}
          />

          {selectedFile && !uploading && !extractedData && (
            <div className="flex justify-center">
              <Button onClick={handleUpload}>
                Rechnung analysieren
              </Button>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-muted-foreground">KI analysiert Rechnung...</span>
            </div>
          )}

          {error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          )}

          {extractedData && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <Card>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Rechnungsnummer</p>
                      <p className="font-medium">{extractedData.invoice_number || "-"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Rechnungsdatum</p>
                      <p className="font-medium">
                        {extractedData.invoice_date
                          ? format(new Date(extractedData.invoice_date), "dd.MM.yyyy", { locale: de })
                          : "-"
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Gesamtbetrag</p>
                      <p className="font-medium">
                        {extractedData.total_amount
                          ? `${extractedData.total_amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`
                          : "-"
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Matching Summary */}
              <div className="flex gap-2 flex-wrap">
                <Badge variant="default" className="gap-1">
                  <Check className="h-3 w-3" />
                  {matchedCount} Fahrzeuge zugeordnet
                </Badge>
                {unmatchedCount > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {unmatchedCount} unbekannt
                  </Badge>
                )}
                {customerCosts.length > 0 && (
                  <Badge variant="outline" className="gap-1">
                    <Info className="h-3 w-3" />
                    {customerCosts.length} allg. Kosten ({customerCostTotal.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €)
                  </Badge>
                )}
              </div>

              {/* Vehicle Totals Table */}
              <ScrollArea className="h-[250px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kennzeichen</TableHead>
                      <TableHead>Kraftstoff</TableHead>
                      <TableHead className="text-right">Liter gesamt</TableHead>
                      <TableHead className="text-right">Positionen</TableHead>
                      <TableHead className="text-right">Summe</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicleTotalsWithMatching.map((vt, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{vt.license_plate || "-"}</TableCell>
                        <TableCell>{vt.fuel_types?.join(", ") || "-"}</TableCell>
                        <TableCell className="text-right">
                          {vt.total_liters ? `${vt.total_liters.toFixed(1)} L` : "-"}
                        </TableCell>
                        <TableCell className="text-right">{vt.position_count}</TableCell>
                        <TableCell className="text-right font-medium">
                          {vt.total_amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                        </TableCell>
                        <TableCell>
                          {vt.matchedVehicle ? (
                            <Badge variant="default" className="gap-1">
                              <Check className="h-3 w-3" />
                              {vt.matchedVehicle.name}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Unbekannt
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Customer Costs */}
              {customerCosts.length > 0 && (
                <Card>
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Kundenbezogene Kosten (werden anteilig auf {matchedCount} Fahrzeuge verteilt)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Beschreibung</TableHead>
                          <TableHead className="text-right">Betrag</TableHead>
                          {matchedCount > 0 && (
                            <TableHead className="text-right">Pro Fahrzeug</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerCosts.map((cc, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{cc.description}</TableCell>
                            <TableCell className="text-right">
                              {cc.amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                            </TableCell>
                            {matchedCount > 0 && (
                              <TableCell className="text-right text-muted-foreground">
                                {(cc.amount / matchedCount).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        <TableRow className="font-medium">
                          <TableCell>Gesamt</TableCell>
                          <TableCell className="text-right">
                            {customerCostTotal.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                          </TableCell>
                          {matchedCount > 0 && (
                            <TableCell className="text-right">
                              {(customerCostTotal / matchedCount).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                            </TableCell>
                          )}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Abbrechen
                </Button>
                {(matchedCount > 0 || customerCostTotal > 0) && (
                  <Button onClick={handleSavePositions} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Kosten übernehmen
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
